import { useState, useCallback, useRef, useEffect } from 'react';
import { PLATFORM_FEE_ADDRESS, calculatePlatformFee, canSendFeeOutput } from '../lib/platform';
import { getReadRelays, getCurrentPubkey } from './useNostrPublish';
import { getStoredStamp, clearStoredStamp, hasStampBeenSwept, markStampSwept } from '../lib/referral';
import { encryptSecret, decryptSecret, isEncryptedValue } from '../lib/keyEncryption';
import { isOwnedBusiness } from '../lib/identities';
import { PUSD_CATEGORY, PUSD_OUTPUT_SATS } from '../lib/pusd';

// ── Mainnet-js singleton ──────────────────────────────────────────
let _mainnetModule: any = null;
let _walletInstance: any = null;
let _loadPromise: Promise<void> | null = null;
let _currentWalletPubkey: string | null = null; // Track which user the wallet belongs to
// In-memory balance cache — seeded from localStorage on module load so the
// very first render shows the last known balance, not 0.
//
// IMPORTANT: seed ONLY from the CURRENT user's balance cache. Identity
// switching reloads the page, so this runs fresh each time with the newly
// active `bchnostr:current-user`. Scanning for "any non-zero balance" (the
// old behaviour) would show the personal account's balance right after
// switching to a business Page — the isolation bug this replaces.
let _cachedBalanceSats: bigint = (() => {
  try {
    const pk = localStorage.getItem('bchnostr:current-user');
    // Business Pages never use the legacy key; only a personal account may.
    const key = pk
      ? `bchnostr:tip-wallet-balance:${pk}`
      : 'bchnostr:tip-wallet-balance';
    const val = localStorage.getItem(key);
    if (val) { const v = BigInt(val); if (v > BigInt(0)) return v; }
    // Legacy fallback for a pre-namespacing personal account only.
    if (pk && !isOwnedBusiness(pk)) {
      const legacy = localStorage.getItem('bchnostr:tip-wallet-balance');
      if (legacy) { const v = BigInt(legacy); if (v > BigInt(0)) return v; }
    }
  } catch {}
  return BigInt(0);
})();
let _initInProgress: Promise<void> | null = null; // Global dedup — prevents concurrent init() calls

const NOSTR_D_TAG = 'bchnostr/tip-wallet';

// Per-user balance cache key
function getBalanceCacheKey(pubkey?: string | null): string {
  return pubkey ? `bchnostr:tip-wallet-balance:${pubkey}` : 'bchnostr:tip-wallet-balance';
}

// Per-user WIF storage key
function getWIFKey(pubkey?: string): string {
  return pubkey ? `bchnostr:tip-wallet-wif:${pubkey}` : 'bchnostr:tip-wallet-wif';
}

// ── Helpers ───────────────────────────────────────────────────────
function parseSats(raw: any): bigint {
  if (typeof raw === 'bigint') return raw;
  if (typeof raw === 'number') return BigInt(Math.round(raw));
  if (typeof raw === 'object' && raw !== null) {
    const v = raw.sat ?? raw.satoshis ?? raw.sats ?? raw.value;
    if (v !== undefined) return typeof v === 'bigint' ? v : BigInt(Number(v) || 0);
  }
  return BigInt(0);
}

// ── Token-aware (CashToken) deposit address ──────────────────────
// CashToken-aware wallets require a "token-aware" CashAddr: the same payload
// as the plain BCH address, but with a token-type prefix so the receiving
// wallet knows CashTokens (e.g. PUSD) are expected. mainnet-js exposes this
// via wallet.getTokenDepositAddress(). We fall back gracefully to the plain
// deposit address if the method is unavailable (older mainnet-js builds).
function deriveTokenAddress(wallet: any): string | null {
  if (!wallet) return null;
  try {
    if (typeof wallet.getTokenDepositAddress === 'function') {
      const t = wallet.getTokenDepositAddress();
      if (typeof t === 'string' && t.length > 0) return t;
    }
  } catch {}
  // Fallback: plain deposit address (not token-aware, but still valid BCH).
  try {
    if (typeof wallet.getDepositAddress === 'function') {
      return wallet.getDepositAddress();
    }
  } catch {}
  return null;
}

// ── PUSD (CashToken) balance ──────────────────────────────────────
// Token balance is read straight from the wallet instance via mainnet-js
// getTokenBalance(category), which queries the Electrum provider for the
// wallet's token UTXOs. Rate-limited + deduplicated like the BCH balance.
//
// The value is in PUSD BASE UNITS (2 decimals), matching on-chain token
// amounts — NOT dollars. 2550n means $25.50.
// Legacy single-value PUSD cache, kept in sync by getTokenBalancesReliable
// so the back-compat `pusdBase` state field keeps working.
let _cachedPusdBase: bigint = BigInt(0);

// ── Generic multi-token balances ─────────────────────────────────
// For the curated tip-token allowlist (PUSD, MUSD, RES, LIFT, FURU, …) we
// read every token balance the wallet holds in ONE call via mainnet-js
// getAllTokenBalances(), which returns { [category]: amountBaseUnits }.
// Values are in each token's own base units. Rate-limited + deduplicated.
type TokenBalances = Record<string, bigint>;
let _cachedTokenBalances: TokenBalances = {};
let _lastTokenFetchTime = 0;
let _tokenFetchInFlight: Promise<TokenBalances> | null = null;
const TOKEN_COOLDOWN_MS = 30_000;

async function _fetchAllTokensRaw(wallet: any): Promise<TokenBalances | null> {
  try {
    if (typeof wallet.getAllTokenBalances !== 'function') return null;
    const raw = await wallet.getAllTokenBalances();
    if (!raw || typeof raw !== 'object') return {};
    const out: TokenBalances = {};
    for (const [category, amount] of Object.entries(raw)) {
      const v = typeof amount === 'bigint' ? amount : BigInt(Math.max(0, Math.floor(Number(amount) || 0)));
      if (v > BigInt(0)) out[category.toLowerCase()] = v;
    }
    return out;
  } catch {
    return null;
  }
}

/**
 * Get ALL token balances with rate limiting + dedup. Returns the cached map
 * on failure (never wipes known balances). Notifies token listeners.
 */
async function getTokenBalancesReliable(wallet: any, force = false): Promise<TokenBalances> {
  const now = Date.now();
  if (!force && now - _lastTokenFetchTime < TOKEN_COOLDOWN_MS) {
    return _cachedTokenBalances;
  }
  if (_tokenFetchInFlight) return _tokenFetchInFlight;

  _tokenFetchInFlight = (async () => {
    try {
      const result = await _fetchAllTokensRaw(wallet);
      _lastTokenFetchTime = Date.now();
      if (result === null) return _cachedTokenBalances; // fetch failed — keep cache
      _cachedTokenBalances = result;
      // Keep the legacy PUSD cache in sync for back-compat.
      _cachedPusdBase = result[PUSD_CATEGORY.toLowerCase()] ?? BigInt(0);
      _tokenListeners.forEach(fn => fn(result));
      _pusdListeners.forEach(fn => fn(_cachedPusdBase));
      return result;
    } finally {
      _tokenFetchInFlight = null;
    }
  })();

  return _tokenFetchInFlight;
}

// ── Rate-limited balance fetcher ─────────────────────────────────
// Prevents hammering bchexplorer.cash and Electrum servers.
// Max 1 real network call per BALANCE_COOLDOWN_MS. Concurrent calls
// are deduplicated into a single in-flight request.
const BALANCE_COOLDOWN_MS = 30_000; // 30 seconds between real fetches
let _lastBalanceFetchTime = 0;
let _balanceFetchInFlight: Promise<bigint> | null = null;

/** Sentinel value meaning "fetch failed — don't overwrite cached balance" */
const BALANCE_FETCH_FAILED = BigInt(-1);

async function _fetchBalanceRaw(wallet: any): Promise<bigint> {
  const addr: string = wallet.getDepositAddress();
  const addrNaked = addr.replace(/^bitcoincash:/i, '');

  // Try multiple APIs for resilience — a single API going down
  // should never cause the balance to disappear.

  // 1) bchexplorer.cash (Blockstream-style API)
  try {
    const target = encodeURIComponent(`https://bchexplorer.cash/api/v1/address/${addrNaked}`);
    const res = await fetch(`https://proxy.shakespeare.diy/?url=${target}`, {
      signal: AbortSignal.timeout(8000),
    });
    if (res.ok) {
      const json = await res.json();
      const confirmed = (json?.chain_stats?.funded_txo_sum ?? 0) - (json?.chain_stats?.spent_txo_sum ?? 0);
      const unconfirmed = (json?.mempool_stats?.funded_txo_sum ?? 0) - (json?.mempool_stats?.spent_txo_sum ?? 0);
      const bal = confirmed + unconfirmed;
      if (bal >= 0) return BigInt(bal);
    }
  } catch {}

  // 2) Fallback: api.blockchair.com
  try {
    const target2 = encodeURIComponent(`https://api.blockchair.com/bitcoin-cash/dashboards/address/${addrNaked}?limit=0`);
    const res2 = await fetch(`https://proxy.shakespeare.diy/?url=${target2}`, {
      signal: AbortSignal.timeout(8000),
    });
    if (res2.ok) {
      const json2 = await res2.json();
      const addrData = json2?.data?.[addrNaked] || json2?.data?.['bitcoincash:' + addrNaked];
      const bal2 = addrData?.address?.balance;
      if (typeof bal2 === 'number' && bal2 >= 0) return BigInt(bal2);
    }
  } catch {}

  // All APIs failed — return sentinel so caller keeps the cached balance.
  return BALANCE_FETCH_FAILED;
}

/**
 * Get balance with rate limiting and deduplication.
 * - If called within 30s of a previous fetch, returns cached value immediately.
 * - If another fetch is already in-flight, piggybacks on that instead of making a new one.
 * - Pass `force: true` to bypass the cooldown (e.g. after a send).
 */
async function getBalanceReliable(wallet: any, force = false): Promise<bigint> {
  const now = Date.now();

  // Return cache if within cooldown and not forced
  if (!force && (now - _lastBalanceFetchTime) < BALANCE_COOLDOWN_MS) {
    return _cachedBalanceSats;
  }

  // Deduplicate concurrent calls
  if (_balanceFetchInFlight) {
    return _balanceFetchInFlight;
  }

  _balanceFetchInFlight = (async () => {
    try {
      const sats = await _fetchBalanceRaw(wallet);

      // If the fetch failed (network error, timeout, etc.), keep the
      // existing cached balance. NEVER overwrite a known-good balance
      // with 0 just because the API was temporarily unreachable.
      if (sats === BALANCE_FETCH_FAILED) {
        // Still update the fetch time so we don't hammer the API
        _lastBalanceFetchTime = Date.now();
        return _cachedBalanceSats;
      }

      _cachedBalanceSats = sats;
      saveCachedBalance(sats, _currentWalletPubkey);
      _lastBalanceFetchTime = Date.now();
      return sats;
    } finally {
      _balanceFetchInFlight = null;
    }
  })();

  return _balanceFetchInFlight;
}

async function loadMainnet(): Promise<void> {
  if (_mainnetModule) return;
  if (_loadPromise) return _loadPromise;
  _loadPromise = (async () => {
    try {
      // Dynamic import of mainnet-js from ESM CDN
      _mainnetModule = await import(
        /* @vite-ignore */
        'https://esm.sh/mainnet-js@4.0.0-next.4?target=esnext'
      );
      if (!_mainnetModule?.Wallet) throw new Error('mainnet-js Wallet class not found');

      // Kill any Electrum connection that mainnet-js may have auto-opened on import
      disconnectElectrum();
    } catch (err) {
      _loadPromise = null;
      throw err;
    }
  })();
  return _loadPromise;
}

/**
 * Kill the global Electrum WebSocket connection that mainnet-js
 * creates automatically. This prevents the reconnect-spam loop
 * when the Electrum server is down. We only use bchexplorer.cash
 * for balance checks; Electrum is only needed by wallet.send()
 * which will lazily reconnect on its own.
 */
function disconnectElectrum() {
  try {
    const g = globalThis as any;
    if (g.BCH) {
      // rpckit-based provider (v4.x)
      if (typeof g.BCH.disconnect === 'function') g.BCH.disconnect();
      else if (typeof g.BCH.close === 'function') g.BCH.close();
      // Underlying connection object
      if (g.BCH.connection) {
        if (typeof g.BCH.connection.disconnect === 'function') g.BCH.connection.disconnect();
        else if (typeof g.BCH.connection.close === 'function') g.BCH.connection.close();
      }
      // Older electrum-cash style
      if (g.BCH.electrum) {
        if (typeof g.BCH.electrum.disconnect === 'function') g.BCH.electrum.disconnect();
        else if (typeof g.BCH.electrum.close === 'function') g.BCH.electrum.close();
      }
    }
  } catch {}
}

/** Preload mainnet-js in the background so it's ready when user wants to tip */
export function preloadWallet() {
  // Start loading the library immediately (non-blocking)
  loadMainnet().catch(() => {});
}

// ── At-rest WIF encryption (optional, PIN-gated) ─────────────────────
//
// By default the WIF lives in localStorage in plaintext (a hot-wallet
// trade-off). A user can OPT IN to encrypting it behind a PIN — the stored
// value then becomes an opaque `bchenc:v1:` envelope that is meaningless
// without the PIN. Once unlocked for a session, the plaintext WIF is held in
// the in-memory `_sessionWIF` map so all the synchronous callers
// (`getWIF`, `hasWallet`, wallet init) keep working exactly as before.
//
// See src/lib/keyEncryption.ts for the crypto (PBKDF2 → AES-GCM, WebCrypto).

/** In-memory plaintext WIF, keyed by pubkey ('' for the legacy no-pubkey slot).
 *  Populated on unlock (or whenever we write a fresh plaintext WIF). Cleared on
 *  wallet delete / user switch / explicit lock. Never persisted. */
const _sessionWIF = new Map<string, string>();

function sessionKey(pubkey?: string): string { return pubkey || ''; }

/**
 * Read the raw stored value for a pubkey.
 *
 * Each identity (personal account + every Business Page) has its OWN wallet,
 * stored under a per-pubkey key. The old non-namespaced key
 * (`bchnostr:tip-wallet-wif`) is a legacy slot from before per-identity
 * wallets existed. We only fall back to it for a PERSONAL account that has no
 * namespaced wallet yet — that's the genuine pre-namespacing user we need to
 * migrate. A Business Page's key is always freshly generated, so it must never
 * inherit the legacy (personal) wallet, or every account would show the same
 * balance. See migrateLegacyWIF() which promotes the legacy key into the
 * namespaced slot exactly once.
 */
function readRawWIF(pubkey?: string): string | null {
  try {
    const raw = localStorage.getItem(getWIFKey(pubkey));
    if (raw != null) return raw;
    // Legacy fallback — personal accounts only, never a Business Page.
    if (pubkey && !isOwnedBusiness(pubkey)) {
      return localStorage.getItem('bchnostr:tip-wallet-wif');
    }
    return null;
  } catch { return null; }
}

/**
 * One-time migration: if a personal account is using the legacy non-namespaced
 * WIF, copy it into its per-pubkey slot and delete the legacy key. This makes
 * the legacy slot stop leaking into other identities. Safe to call repeatedly.
 */
function migrateLegacyWIF(pubkey?: string): void {
  if (!pubkey || isOwnedBusiness(pubkey)) return;
  try {
    // Already has a namespaced wallet — nothing to migrate.
    if (localStorage.getItem(getWIFKey(pubkey)) != null) return;
    const legacy = localStorage.getItem('bchnostr:tip-wallet-wif');
    if (legacy == null) return;
    localStorage.setItem(getWIFKey(pubkey), legacy);
    localStorage.removeItem('bchnostr:tip-wallet-wif');
    // Migrate the legacy balance cache alongside it, if present.
    const legacyBal = localStorage.getItem('bchnostr:tip-wallet-balance');
    if (legacyBal != null && localStorage.getItem(getBalanceCacheKey(pubkey)) == null) {
      localStorage.setItem(getBalanceCacheKey(pubkey), legacyBal);
    }
    localStorage.removeItem('bchnostr:tip-wallet-balance');
  } catch { /* storage disabled — feature degrades, never throws */ }
}

/**
 * Unwrap a raw stored value (which may be a JSON-encoded plaintext WIF, a bare
 * plaintext WIF, or an encryption envelope) into either the plaintext WIF or,
 * for a locked envelope, `null`.
 */
function decodeStoredWIF(raw: string | null, pubkey?: string): string | null {
  if (!raw) return null;
  if (isEncryptedValue(raw)) {
    // Encrypted — only available after a successful unlock this session.
    return _sessionWIF.get(sessionKey(pubkey)) ?? null;
  }
  // Plaintext: historically stored JSON-encoded (`"K..."`). Be tolerant of a
  // bare string too, in case a raw WIF was ever written directly.
  try {
    const parsed = JSON.parse(raw);
    return typeof parsed === 'string' ? parsed : raw;
  } catch {
    return raw;
  }
}

/** True when the stored WIF for this user is an encrypted envelope. */
function isWIFEncryptedStored(pubkey?: string): boolean {
  return isEncryptedValue(readRawWIF(pubkey));
}

/** True when we can hand out the plaintext WIF right now (plaintext, or
 *  encrypted AND already unlocked this session). */
function isWIFUnlockedStored(pubkey?: string): boolean {
  const raw = readRawWIF(pubkey);
  if (!raw) return false;
  if (!isEncryptedValue(raw)) return true;
  return _sessionWIF.has(sessionKey(pubkey));
}

/**
 * Persist a plaintext WIF and seed the in-memory session copy.
 *
 * NOTE: this writes PLAINTEXT. It is the low-level primitive used by wallet
 * init / import / generate. If the user has PIN encryption enabled and imports
 * or generates a *new* key, the caller is responsible for re-sealing it via
 * `enableWIFEncryption` (the Settings UI does this) — we can't re-encrypt here
 * because we don't hold the PIN. Until then the new key sits plaintext, which
 * matches the legacy default and is surfaced to the user in the UI.
 */
function cacheWIF(wif: string, pubkey?: string) {
  try {
    _sessionWIF.set(sessionKey(pubkey), wif);
    localStorage.setItem(getWIFKey(pubkey), JSON.stringify(wif));
  } catch {}
}

/**
 * Synchronous read of the usable plaintext WIF. Returns null when nothing is
 * stored OR when an encrypted WIF exists but has not been unlocked this
 * session — callers should treat null-with-`isWIFEncryptedStored()` as
 * "locked, prompt for PIN" rather than "no wallet".
 */
function readCachedWIF(pubkey?: string): string | null {
  // Prefer the session plaintext if we have it (covers the unlocked-encrypted
  // case, and avoids re-parsing on every render).
  const cached = _sessionWIF.get(sessionKey(pubkey));
  if (cached) return cached;
  const decoded = decodeStoredWIF(readRawWIF(pubkey), pubkey);
  if (decoded) _sessionWIF.set(sessionKey(pubkey), decoded);
  return decoded;
}

function clearCachedWIF(pubkey?: string) {
  try {
    _sessionWIF.delete(sessionKey(pubkey));
    localStorage.removeItem(getWIFKey(pubkey));
  } catch {}
}

// ── Encryption lifecycle (turn on / off / unlock / re-key) ───────────

/** Enable PIN encryption for the stored WIF. Requires the plaintext WIF and a
 *  PIN. Replaces the plaintext in storage with an envelope; keeps the session
 *  unlocked so the wallet stays usable. */
async function enableWIFEncryption(wif: string, pin: string, pubkey?: string): Promise<void> {
  const envelope = await encryptSecret(wif, pin);
  localStorage.setItem(getWIFKey(pubkey), envelope);
  _sessionWIF.set(sessionKey(pubkey), wif);
}

/** Disable encryption, reverting to plaintext. Requires the PIN to prove the
 *  caller can decrypt (and to obtain the plaintext). */
async function disableWIFEncryption(pin: string, pubkey?: string): Promise<void> {
  const raw = readRawWIF(pubkey);
  if (!raw) throw new Error('No stored wallet key.');
  const plain = isEncryptedValue(raw) ? await decryptSecret(raw, pin) : (decodeStoredWIF(raw, pubkey) || '');
  localStorage.setItem(getWIFKey(pubkey), JSON.stringify(plain));
  _sessionWIF.set(sessionKey(pubkey), plain);
}

/** Unlock an encrypted WIF with the PIN, caching the plaintext for the
 *  session. Returns the plaintext. Throws on a wrong PIN. */
async function unlockWIF(pin: string, pubkey?: string): Promise<string> {
  const raw = readRawWIF(pubkey);
  if (!raw) throw new Error('No stored wallet key to unlock.');
  if (!isEncryptedValue(raw)) {
    const plain = decodeStoredWIF(raw, pubkey) || '';
    _sessionWIF.set(sessionKey(pubkey), plain);
    return plain;
  }
  const plain = await decryptSecret(raw, pin);
  _sessionWIF.set(sessionKey(pubkey), plain);
  return plain;
}

/** Re-key: decrypt with the old PIN, re-encrypt with the new one. */
async function changeWIFPin(oldPin: string, newPin: string, pubkey?: string): Promise<void> {
  const raw = readRawWIF(pubkey);
  if (!raw || !isEncryptedValue(raw)) throw new Error('No encrypted wallet key to re-key.');
  const plain = await decryptSecret(raw, oldPin);
  const envelope = await encryptSecret(plain, newPin);
  localStorage.setItem(getWIFKey(pubkey), envelope);
  _sessionWIF.set(sessionKey(pubkey), plain);
}

/** Drop the in-memory plaintext for a user without deleting the wallet
 *  ("lock now"). */
function lockWIFSession(pubkey?: string) {
  _sessionWIF.delete(sessionKey(pubkey));
}

// ── Balance cache (persists across dialog opens, per-user) ───────
function loadCachedBalance(pubkey?: string | null): bigint {
  try {
    const raw = localStorage.getItem(getBalanceCacheKey(pubkey));
    return raw ? BigInt(raw) : BigInt(0);
  } catch { return BigInt(0); }
}
function saveCachedBalance(sats: bigint, pubkey?: string | null) {
  try { localStorage.setItem(getBalanceCacheKey(pubkey), sats.toString()); } catch {}
}
function clearCachedBalance(pubkey?: string | null) {
  try { localStorage.removeItem(getBalanceCacheKey(pubkey)); } catch {}
}

// ── Nostr WIF sync helpers ────────────────────────────────────────
async function publishWIFToNostr(wif: string): Promise<void> {
  try {
    const signer = (window as any).nostr;
    if (!signer?.nip44?.encrypt) {
      console.log('[TipWallet] No NIP-44 signer — skipping relay WIF publish');
      return;
    }
    const pubkey = await signer.getPublicKey();
    const encrypted = await signer.nip44.encrypt(pubkey, wif);
    const unsigned = {
      kind: 30078,
      content: encrypted,
      tags: [
        ['d', NOSTR_D_TAG],
        ['alt', 'Encrypted BCH tip wallet (NIP-44)'],
      ],
      created_at: Math.floor(Date.now() / 1000),
    };
    const signed = await signer.signEvent(unsigned);

    // Publish to relays — include purplepag.es for reliable cross-device sync
    const baseRelays = getReadRelays().slice(0, 4);
    const relays = [...new Set([...baseRelays, 'wss://purplepag.es'])].slice(0, 6);
    let accepted = 0;

    console.log('[TipWallet] Publishing WIF to', relays.length, 'relays...');

    await Promise.allSettled(
      relays.map(url => new Promise<void>(resolve => {
        try {
          const ws = new WebSocket(url);
          let resolved = false;
          const done = () => { if (resolved) return; resolved = true; try { ws.close(); } catch {} resolve(); };

          ws.onopen = () => {
            ws.send(JSON.stringify(['EVENT', signed]));
          };
          ws.onmessage = (msg) => {
            try {
              const data = JSON.parse(msg.data);
              if (Array.isArray(data) && data[0] === 'OK' && data[2] === true) {
                accepted++;
                console.log('[TipWallet] WIF accepted by', url);
              }
            } catch {}
            // Close after any response
            setTimeout(done, 500);
          };
          ws.onerror = () => done();
          setTimeout(done, 6000);
        } catch { resolve(); }
      }))
    );

    console.log('[TipWallet] WIF published to', accepted, '/', relays.length, 'relays');
  } catch (err) {
    console.warn('[TipWallet] Failed to sync wallet to Nostr:', err);
  }
}

async function fetchWIFFromNostr(): Promise<string | null> {
  try {
    const signer = (window as any).nostr;
    if (!signer?.nip44?.decrypt) {
      console.log('[TipWallet] No NIP-44 signer — skipping relay WIF fetch');
      return null;
    }
    const pubkey = await signer.getPublicKey();

    // Include purplepag.es for reliable cross-device sync (same as publish)
    const baseRelays = getReadRelays().slice(0, 4);
    const relays = [...new Set([...baseRelays, 'wss://purplepag.es'])].slice(0, 6);
    let bestEvent: any = null;
    let relaysResponded = 0;

    console.log('[TipWallet] Fetching WIF from', relays.length, 'relays...');

    await Promise.allSettled(
      relays.map(url => new Promise<void>(resolve => {
        try {
          const ws = new WebSocket(url);
          const subId = 'wif_' + Math.random().toString(36).slice(2, 8);
          let resolved = false;
          const done = () => { if (resolved) return; resolved = true; try { ws.close(); } catch {} resolve(); };

          ws.onopen = () => {
            ws.send(JSON.stringify(['REQ', subId, {
              kinds: [30078],
              authors: [pubkey],
              '#d': [NOSTR_D_TAG],
              limit: 1,
            }]));
          };
          ws.onmessage = (msg) => {
            try {
              const data = JSON.parse(msg.data);
              if (data[0] === 'EVENT' && data[1] === subId) {
                const ev = data[2];
                if (!bestEvent || ev.created_at > bestEvent.created_at) {
                  bestEvent = ev;
                }
                console.log('[TipWallet] Got WIF event from', url, 'ts:', ev.created_at);
              }
              if (data[0] === 'EOSE' && data[1] === subId) {
                relaysResponded++;
                done();
              }
            } catch {}
          };
          ws.onerror = () => { console.log('[TipWallet] Relay error:', url); done(); };
          ws.onclose = () => done();
          // 8s timeout — mobile/PWA connections can be slow
          setTimeout(done, 8000);
        } catch { resolve(); }
      }))
    );

    console.log('[TipWallet] WIF fetch done.', relaysResponded, 'relays responded, event:', bestEvent ? 'found' : 'none');

    if (!bestEvent?.content) return null;
    const wif = await signer.nip44.decrypt(pubkey, bestEvent.content);
    // Reject deleted wallets and invalid WIFs
    if (!wif || wif === '__DELETED__') {
      console.log('[TipWallet] WIF from relay is deleted/empty');
      return null;
    }
    if (wif.startsWith('K') || wif.startsWith('L') || wif.startsWith('5')) {
      console.log('[TipWallet] Valid WIF recovered from relay:', wif.slice(0, 4) + '...');
      return wif;
    }
    console.warn('[TipWallet] WIF from relay has invalid format:', wif.slice(0, 4) + '...');
    return null;
  } catch (err) {
    console.error('[TipWallet] fetchWIFFromNostr error:', err);
    return null;
  }
}

// ── CashStamp Sweep ───────────────────────────────────────────────
// When a user lands via ?stamp=WIF, the WIF is stored in localStorage.
// After their tip wallet initializes, we create a temporary wallet
// from the stamp WIF and send all its funds to the user's tip wallet.
// This is a fire-and-forget background operation.
//
// The stamp sweep result is stored in a global so the UI can show
// a success notification. Components can read it via getStampSweepResult().
let _stampSweepResult: { amountSats: number; txid: string } | null = null;
const _stampSweepListeners = new Set<(result: { amountSats: number; txid: string }) => void>();

/** Register a listener for stamp sweep completion */
export function onStampSwept(fn: (result: { amountSats: number; txid: string }) => void): () => void {
  _stampSweepListeners.add(fn);
  // If already swept this session, fire immediately
  if (_stampSweepResult) fn(_stampSweepResult);
  return () => { _stampSweepListeners.delete(fn); };
}

/** Get the stamp sweep result if it happened this session */
export function getStampSweepResult(): { amountSats: number; txid: string } | null {
  return _stampSweepResult;
}

/**
 * Fetch stamp balance directly via API — does NOT use the global rate-limited
 * cache (which belongs to the user's tip wallet and would return the wrong value).
 */
async function fetchStampBalance(address: string): Promise<bigint> {
  const naked = address.replace(/^bitcoincash:/i, '');

  // 1) bchexplorer.cash
  try {
    const target = encodeURIComponent(`https://bchexplorer.cash/api/v1/address/${naked}`);
    const res = await fetch(`https://proxy.shakespeare.diy/?url=${target}`, {
      signal: AbortSignal.timeout(10000),
    });
    if (res.ok) {
      const json = await res.json();
      const confirmed = (json?.chain_stats?.funded_txo_sum ?? 0) - (json?.chain_stats?.spent_txo_sum ?? 0);
      const unconfirmed = (json?.mempool_stats?.funded_txo_sum ?? 0) - (json?.mempool_stats?.spent_txo_sum ?? 0);
      const bal = confirmed + unconfirmed;
      if (bal >= 0) return BigInt(bal);
    }
  } catch {}

  // 2) Fallback: blockchair
  try {
    const target2 = encodeURIComponent(`https://api.blockchair.com/bitcoin-cash/dashboards/address/${naked}?limit=0`);
    const res2 = await fetch(`https://proxy.shakespeare.diy/?url=${target2}`, {
      signal: AbortSignal.timeout(10000),
    });
    if (res2.ok) {
      const json2 = await res2.json();
      const addrData = json2?.data?.[naked] || json2?.data?.['bitcoincash:' + naked];
      const bal2 = addrData?.address?.balance;
      if (typeof bal2 === 'number' && bal2 >= 0) return BigInt(bal2);
    }
  } catch {}

  return BigInt(-1); // unknown
}

async function sweepStampIfPending(userWallet: any): Promise<void> {
  // Check if there's a stamp to sweep
  const stampWif = getStoredStamp();
  if (!stampWif || hasStampBeenSwept(stampWif)) return;

  try {
    await loadMainnet();
    const { Wallet } = _mainnetModule;

    // Create a temporary wallet from the stamp's WIF
    const stampWallet = await Wallet.fromWIF(stampWif);
    disconnectElectrum();

    // Check the stamp's balance using a DEDICATED fetch (not the global rate-limited one)
    const stampAddr: string = stampWallet.getDepositAddress();
    const stampBalance = await fetchStampBalance(stampAddr);

    if (stampBalance === BigInt(-1)) {
      // API failed — don't assume empty, retry next session
      console.warn('CashStamp: could not verify balance, will retry later');
      return;
    }

    if (stampBalance <= BigInt(600)) {
      // Nothing worth sweeping (dust or already swept)
      clearStoredStamp();
      markStampSwept(stampWif);
      return;
    }

    // Sweep everything to the user's tip wallet address using sendMax.
    // sendMax sends the entire balance minus the miner fee — no manual fee guess needed.
    const userAddr: string = userWallet.getDepositAddress();

    let result: any;
    try {
      // Try sendMax first (sends entire balance minus miner fee)
      result = await stampWallet.sendMax(userAddr);
    } catch {
      // Fallback: manual send with conservative fee estimate
      const sendAmount = stampBalance - BigInt(500);
      if (sendAmount <= BigInt(0)) {
        clearStoredStamp();
        markStampSwept(stampWif);
        return;
      }
      result = await stampWallet.send([{
        cashaddr: userAddr,
        value: Number(sendAmount), // mainnet-js expects number, not BigInt
        unit: 'sat',
      }]);
    }
    disconnectElectrum();

    // Mark as swept so we don't try again
    clearStoredStamp();
    markStampSwept(stampWif);

    // Calculate swept amount from tx result or estimate
    const sweptSats = result?.balance !== undefined
      ? Number(stampBalance) - Number(parseSats(result.balance))
      : Number(stampBalance) - 300;
    console.log(`CashStamp swept: ~${sweptSats} sats (tx: ${result?.txId})`);

    // Update the user wallet balance
    const addedSats = BigInt(Math.max(0, sweptSats));
    const newBalance = _cachedBalanceSats + addedSats;
    notifyBalanceChange(newBalance, true);

    // Schedule a proper balance refresh after the tx propagates
    setTimeout(() => {
      if (_walletInstance) {
        getBalanceReliable(_walletInstance, true).then(sats => {
          notifyBalanceChange(sats);
        }).catch(() => {});
      }
    }, 5000);

    // Notify listeners (for UI toast)
    _stampSweepResult = { amountSats: Math.max(0, sweptSats), txid: result?.txId || '' };
    _stampSweepListeners.forEach(fn => fn(_stampSweepResult!));
  } catch (err) {
    console.error('CashStamp sweep failed:', err);
    // Don't clear the stamp on error — let them retry next session
  }
}

// ── The Hook ──────────────────────────────────────────────────────
export interface TipWalletState {
  ready: boolean;
  loading: boolean;
  address: string | null;
  fullAddress: string | null;
  /** CashToken-aware deposit address (token-type CashAddr prefix), naked —
   *  i.e. without the `bitcoincash:` scheme. Use this when receiving
   *  CashTokens (e.g. PUSD) so token-aware senders know tokens are expected. */
  tokenAddress: string | null;
  /** Full CashToken-aware deposit address including the `bitcoincash:` scheme. */
  fullTokenAddress: string | null;
  balanceSats: bigint;
  /** PUSD (ParyonUSD CashToken) balance in BASE UNITS (2 decimals).
   *  2550n = $25.50. Updated lazily in the background like balanceSats.
   *  Kept for back-compat; prefer `tokenBalances[PUSD_CATEGORY]`. */
  pusdBase: bigint;
  /** All CashToken balances held by the wallet, keyed by lowercase category
   *  id, in each token's own base units. Populated in the background. */
  tokenBalances: Record<string, bigint>;
  sending: boolean;
  error: string | null;
  /** True when the stored WIF is PIN-encrypted and hasn't been unlocked this
   *  session. The wallet cannot initialize until `unlock(pin)` succeeds. */
  locked: boolean;
  /** True when the stored WIF is protected by a PIN (locked or unlocked). */
  encrypted: boolean;
}

export interface TipWallet extends TipWalletState {
  init: () => Promise<void>;
  refreshBalance: () => Promise<bigint>;
  sendBCH: (toAddress: string, amountSats: bigint) => Promise<string>;
  /** Send to multiple outputs in a single transaction */
  sendMultiOutput: (outputs: { cashaddr: string; value: bigint }[]) => Promise<string>;
  /** Send tip to recipient + 1% platform fee in a single transaction */
  sendTipWithFee: (recipientAddress: string, tipSats: bigint) => Promise<string>;
  getMaxSend: () => Promise<bigint>;
  /** Force-refresh the PUSD token balance (base units). */
  refreshPusdBalance: () => Promise<bigint>;
  /** Force-refresh ALL token balances (map keyed by lowercase category). */
  refreshTokenBalances: () => Promise<Record<string, bigint>>;
  /** Send PUSD tokens to an address. `amountBase` is in PUSD base units
   *  (2 decimals). The recipient gets the full token amount; the wallet must
   *  hold enough BCH to fund the ~1000-sat token dust + miner fee + fee. Pass
   *  `{ feeSats }` to add the 1% platform fee as a BCH output. Returns txid. */
  sendPusd: (toAddress: string, amountBase: bigint, opts?: { feeSats?: bigint }) => Promise<string>;
  /** Send any CashToken. `category` is the 64-char hex genesis id, `amountBase`
   *  is in that token's base units. The recipient gets the full amount; pass
   *  `{ feeSats }` to add the 1% platform fee as a separate BCH output.
   *  Returns the txid. */
  sendToken: (toAddress: string, category: string, amountBase: bigint, opts?: { feeSats?: bigint }) => Promise<string>;
  getWIF: () => string | null;
  clearWallet: () => void;
  /** Generate a brand new random wallet (replaces current) */
  generateNewWallet: () => Promise<void>;
  importWIF: (wif: string) => Promise<void>;
  /** Import wallet from 12 or 24-word mnemonic seed phrase */
  importMnemonic: (mnemonic: string, derivationPath?: string) => Promise<void>;
  hasWallet: boolean;
  // ── At-rest PIN encryption of the WIF ──────────────────────────────
  /** Encrypt the stored WIF behind a PIN (requires the wallet to be unlocked). */
  encryptWallet: (pin: string) => Promise<void>;
  /** Remove PIN encryption, reverting the WIF to plaintext storage. */
  decryptWallet: (pin: string) => Promise<void>;
  /** Unlock an encrypted WIF for this session, then initialize the wallet. */
  unlockWallet: (pin: string) => Promise<void>;
  /** Change the encryption PIN (decrypt with old, re-encrypt with new). */
  changeWalletPin: (oldPin: string, newPin: string) => Promise<void>;
  /** Forget the decrypted WIF in memory without deleting the wallet. */
  lockWallet: () => void;
}

/** Current encryption flags for a pubkey, for spreading into state objects. */
function encFlags(pubkey?: string | null): { locked: boolean; encrypted: boolean } {
  const pk = pubkey || undefined;
  const encrypted = isWIFEncryptedStored(pk);
  return { encrypted, locked: encrypted && !isWIFUnlockedStored(pk) };
}

// ── Global state subscribers ─────────────────────────────────────
// When any useTipWallet instance updates the balance, notify all
// other mounted instances so the sidebar stays in sync.
type BalanceListener = (sats: bigint) => void;
const _balanceListeners = new Set<BalanceListener>();

// Separate subscriber set for PUSD (token) balance changes.
type PusdListener = (base: bigint) => void;
const _pusdListeners = new Set<PusdListener>();

// Subscriber set for the full multi-token balance map.
type TokenBalancesListener = (balances: Record<string, bigint>) => void;
const _tokenListeners = new Set<TokenBalancesListener>();

/**
 * Update the balance across all useTipWallet instances.
 * @param sats - new balance in satoshis
 * @param trusted - if true, allow setting to 0 (e.g. after send, new wallet).
 *                  if false (default), refuse to overwrite a positive balance with 0
 *                  to prevent transient API failures from zeroing out the display.
 */
function notifyBalanceChange(sats: bigint, trusted = false) {
  // Guard: never overwrite a known positive balance with 0 from an untrusted source.
  // The only trusted sources are: send operations, wallet generation, wallet deletion.
  if (!trusted && sats === BigInt(0) && _cachedBalanceSats > BigInt(0)) {
    return; // Silently ignore — keep the known-good balance
  }
  _cachedBalanceSats = sats;
  saveCachedBalance(sats, _currentWalletPubkey);
  _balanceListeners.forEach(fn => fn(sats));
}

export function useTipWallet(): TipWallet {
  // Initialize with the best available cached balance so we never flash "0"
  const [state, setState] = useState<TipWalletState>(() => {
    const cached = _cachedBalanceSats > BigInt(0) ? _cachedBalanceSats : loadCachedBalance(_currentWalletPubkey);
    return {
      ready: false,
      loading: false,
      address: null,
      fullAddress: null,
      tokenAddress: null,
      fullTokenAddress: null,
      balanceSats: cached,
      pusdBase: _cachedPusdBase,
      tokenBalances: _cachedTokenBalances,
      sending: false,
      error: null,
      ...encFlags(_currentWalletPubkey),
    };
  });
  const initRef = useRef(false);

  // Subscribe to balance changes from other useTipWallet instances
  useEffect(() => {
    const listener: BalanceListener = (sats) => {
      setState(s => ({ ...s, balanceSats: sats }));
    };
    _balanceListeners.add(listener);
    return () => { _balanceListeners.delete(listener); };
  }, []);

  // Subscribe to PUSD (token) balance changes across instances
  useEffect(() => {
    const listener: PusdListener = (base) => {
      setState(s => ({ ...s, pusdBase: base }));
    };
    _pusdListeners.add(listener);
    return () => { _pusdListeners.delete(listener); };
  }, []);

  // Subscribe to the full token-balance map across instances
  useEffect(() => {
    const listener: TokenBalancesListener = (balances) => {
      setState(s => ({ ...s, tokenBalances: balances }));
    };
    _tokenListeners.add(listener);
    return () => { _tokenListeners.delete(listener); };
  }, []);

  const init = useCallback(async () => {
    // Deduplicate concurrent init() calls across all useTipWallet instances
    if (_initInProgress) {
      await _initInProgress;
      // After the in-flight init completes, sync state from module-level wallet
      if (_walletInstance) {
        const addr: string = _walletInstance.getDepositAddress();
        const cachedBalance = _cachedBalanceSats > BigInt(0) ? _cachedBalanceSats : loadCachedBalance(_currentWalletPubkey);
        const tokenAddr = deriveTokenAddress(_walletInstance);
        setState({
          ready: true, loading: false,
          address: addr.replace(/^bitcoincash:/i, ''),
          fullAddress: addr,
          tokenAddress: tokenAddr ? tokenAddr.replace(/^bitcoincash:/i, '') : null,
          fullTokenAddress: tokenAddr,
          balanceSats: cachedBalance,
          pusdBase: _cachedPusdBase,
          tokenBalances: _cachedTokenBalances,
          sending: false, error: null,
          ...encFlags(_currentWalletPubkey),
        });
      }
      return;
    }

    // Get current user pubkey — from localStorage, NOT the signer. We only
    // need the identity here (to detect a user switch); asking the signer for
    // it would add an avoidable extension/bunker round-trip to every init().
    const currentPubkey: string | null = getCurrentPubkey();

    // Promote any legacy (pre-namespacing) personal wallet into its
    // per-pubkey slot and drop the shared legacy key, so it can never leak
    // into another identity. No-op for Business Pages and already-migrated
    // users.
    migrateLegacyWIF(currentPubkey || undefined);

    // If user changed, reset wallet completely
    if (_currentWalletPubkey && currentPubkey && _currentWalletPubkey !== currentPubkey) {
      _walletInstance = null;
      initRef.current = false;
      _cachedBalanceSats = BigInt(0);
      _lastBalanceFetchTime = 0;
      setState({
        ready: false, loading: false, address: null, fullAddress: null,
        tokenAddress: null, fullTokenAddress: null,
        balanceSats: BigInt(0), sending: false, error: null,
        pusdBase: BigInt(0),
        tokenBalances: {},
        ...encFlags(currentPubkey),
      });
    }

    if (initRef.current && _currentWalletPubkey === currentPubkey) return;
    initRef.current = true;
    _currentWalletPubkey = currentPubkey;

    // Fast path: wallet instance already exists from another hook instance
    if (_walletInstance) {
      const addr: string = _walletInstance.getDepositAddress();
      const cachedBalance = _cachedBalanceSats > BigInt(0) ? _cachedBalanceSats : loadCachedBalance(currentPubkey);
      const tokenAddr = deriveTokenAddress(_walletInstance);
      setState({
        ready: true, loading: false,
        address: addr.replace(/^bitcoincash:/i, ''),
        fullAddress: addr,
        tokenAddress: tokenAddr ? tokenAddr.replace(/^bitcoincash:/i, '') : null,
        fullTokenAddress: tokenAddr,
        balanceSats: cachedBalance,
        pusdBase: _cachedPusdBase,
        tokenBalances: _cachedTokenBalances,
        sending: false, error: null,
        ...encFlags(currentPubkey),
      });
      return;
    }

    // Locked-encrypted guard: if the stored WIF is encrypted and not yet
    // unlocked this session, we CANNOT proceed — decrypting requires the PIN.
    // Surface a locked state and bail; the UI must call unlockWallet(pin).
    if (isWIFEncryptedStored(currentPubkey || undefined) && !isWIFUnlockedStored(currentPubkey || undefined)) {
      initRef.current = false; // allow re-init after unlock
      setState(s => ({
        ...s, ready: false, loading: false,
        error: null, locked: true, encrypted: true,
      }));
      return;
    }

    setState(s => ({ ...s, loading: true, error: null }));

    _initInProgress = (async () => {
      try {
        await loadMainnet();
        const { Wallet } = _mainnetModule;

        let wif: string | null = null;

        // 1) Check localStorage first — instant, no network call
        wif = readCachedWIF(currentPubkey || undefined);
        console.log('[TipWallet] Init step 1 — localStorage WIF:', wif ? wif.slice(0, 4) + '...' : 'none');

        // 2) Only hit Nostr relays if nothing in localStorage
        //    (first login on a new device, or cache was cleared)
        if (!wif && (window as any).nostr) {
          console.log('[TipWallet] Init step 2 — fetching WIF from Nostr relays...');
          wif = await fetchWIFFromNostr();
          if (wif) {
            console.log('[TipWallet] Init step 2 — recovered WIF from relay, caching locally');
            cacheWIF(wif, currentPubkey || undefined);
          }
        }

        // 3) If we found a local WIF but haven't synced to Nostr yet, do it in background
        if (wif && (window as any).nostr) {
          publishWIFToNostr(wif).catch(() => {}); // fire-and-forget
        }

        // 4) Create a brand new wallet only if nothing found anywhere
        if (!wif) {
          console.log('[TipWallet] Init step 4 — no WIF found anywhere, creating new wallet');
          if (!(window as any).nostr) {
            setState(s => ({ ...s, loading: false, error: 'Log in to create a tip wallet' }));
            initRef.current = false;
            return;
          }
          const newWallet = await Wallet.newRandom();
          wif = newWallet.privateKeyWif;
          cacheWIF(wif, currentPubkey || undefined);
          publishWIFToNostr(wif).catch(() => {}); // fire-and-forget
        }

        const wallet = await Wallet.fromWIF(wif);
        _walletInstance = wallet;

        // Kill the Electrum WebSocket that fromWIF() auto-creates.
        // We don't need it for balance checks. send() will reconnect lazily.
        disconnectElectrum();

        const addr: string = wallet.getDepositAddress();
        const displayAddr = addr.replace(/^bitcoincash:/i, '');
        const tokenAddr = deriveTokenAddress(wallet);

        // Use cached balance for instant display — refresh in background
        const cachedBalance = _cachedBalanceSats > BigInt(0)
          ? _cachedBalanceSats
          : loadCachedBalance(currentPubkey);

        setState({
          ready: true,
          loading: false,
          address: displayAddr,
          fullAddress: addr,
          tokenAddress: tokenAddr ? tokenAddr.replace(/^bitcoincash:/i, '') : null,
          fullTokenAddress: tokenAddr,
          balanceSats: cachedBalance,
          pusdBase: _cachedPusdBase,
          tokenBalances: _cachedTokenBalances,
          sending: false,
          error: null,
          ...encFlags(currentPubkey),
        });

        // Refresh balance in background (rate-limited — won't spam)
        getBalanceReliable(wallet).then(sats => {
          notifyBalanceChange(sats);
        }).catch(() => {});

        // Refresh ALL token balances in background too. Failures are
        // swallowed — a missing token balance must never block the wallet.
        getTokenBalancesReliable(wallet).catch(() => {});

        // ── CashStamp sweep ────────────────────────────────────────
        // If the user arrived via a ?stamp=WIF link, sweep the funds
        // from the stamp into their tip wallet in the background.
        sweepStampIfPending(wallet).catch(() => {});
      } catch (err) {
        console.error('Tip wallet init error:', err);
        setState(s => ({
          ...s,
          loading: false,
          error: err instanceof Error ? err.message : 'Failed to initialize wallet',
        }));
        initRef.current = false;
      } finally {
        _initInProgress = null;
      }
    })();

    await _initInProgress;
  }, []);

  const refreshBalance = useCallback(async (): Promise<bigint> => {
    if (!_walletInstance) return BigInt(0);
    try {
      // Force a fresh fetch (bypasses cooldown) — but still deduplicates concurrent calls
      const sats = await getBalanceReliable(_walletInstance, true);
      notifyBalanceChange(sats);
      return sats;
    } catch (err) {
      console.error('Balance refresh error:', err);
      return BigInt(0);
    }
  }, []);

  const refreshPusdBalance = useCallback(async (): Promise<bigint> => {
    if (!_walletInstance) return BigInt(0);
    try {
      const all = await getTokenBalancesReliable(_walletInstance, true);
      const base = all[PUSD_CATEGORY.toLowerCase()] ?? BigInt(0);
      setState(s => ({ ...s, pusdBase: base, tokenBalances: all }));
      return base;
    } catch (err) {
      console.error('PUSD balance refresh error:', err);
      return _cachedPusdBase;
    }
  }, []);

  const refreshTokenBalances = useCallback(async (): Promise<Record<string, bigint>> => {
    if (!_walletInstance) return {};
    try {
      const all = await getTokenBalancesReliable(_walletInstance, true);
      setState(s => ({ ...s, tokenBalances: all, pusdBase: all[PUSD_CATEGORY.toLowerCase()] ?? BigInt(0) }));
      return all;
    } catch (err) {
      console.error('Token balance refresh error:', err);
      return _cachedTokenBalances;
    }
  }, []);

  /**
   * Send any CashToken to a single address.
   *
   * `category` is the token's 64-char hex genesis id; `amountBase` is in that
   * token's own base units. The recipient always receives the FULL token
   * amount in one token output (~1000 sats of BCH dust).
   *
   * The platform fee is taken in **BCH**, not the token: pass `opts.feeSats`
   * (1% of the tip's USD value, converted to satoshis by the caller using the
   * live BCH price) and it is added as a separate BCH output to
   * PLATFORM_FEE_ADDRESS. Collecting in BCH avoids token decimal rounding, so
   * even a micro-tip yields a real fee. The fee output is skipped if it would
   * fall below the BCH dust limit (546 sats).
   *
   * All BCH (token dust + fee + miner fee) comes from the wallet's BCH balance.
   */
  const sendToken = useCallback(async (
    toAddress: string,
    category: string,
    amountBase: bigint,
    opts?: { feeSats?: bigint },
  ): Promise<string> => {
    if (!_walletInstance) throw new Error('Wallet not ready');
    if (amountBase <= BigInt(0)) throw new Error('Token amount too small');
    if (!/^[0-9a-f]{64}$/i.test(category)) throw new Error('Invalid token category');

    // Validate the cashaddr up front for a clear error.
    const normalized = toAddress.startsWith('bitcoincash:') ? toAddress : `bitcoincash:${toAddress}`;
    const payload = normalized.slice('bitcoincash:'.length);
    // Accept both plain (q/p) and token-aware (z/r) CashAddr payloads.
    if (!/^[qpzr][qpzry9x8gf2tvdw0s3jn54khce6mua7l]{41,}$/.test(payload)) {
      throw new Error('Invalid cashaddr: malformed payload');
    }

    const { TokenSendRequest, SendRequest } = _mainnetModule;
    if (!TokenSendRequest) throw new Error('CashToken support unavailable');

    // BCH platform fee (satoshis). Only emitted as its own output when it
    // clears the dust limit — otherwise it's absorbed as extra miner fee.
    const feeSats = opts?.feeSats && opts.feeSats > BigInt(0) ? opts.feeSats : BigInt(0);

    // Recreate wallet from WIF for fresh UTXOs (avoids mempool conflicts).
    try {
      const wif = _walletInstance.privateKeyWif;
      if (wif) _walletInstance = await _mainnetModule.Wallet.fromWIF(wif);
    } catch {}

    setState(s => ({ ...s, sending: true, error: null }));

    try {
      const outputs: any[] = [
        new TokenSendRequest({
          cashaddr: normalized,
          category: category.toLowerCase(),
          amount: amountBase,
          value: PUSD_OUTPUT_SATS,
        }),
      ];
      // BCH fee output — only when it clears the dust limit (546 sats).
      if (canSendFeeOutput(Number(feeSats)) && SendRequest) {
        outputs.push(new SendRequest({
          cashaddr: PLATFORM_FEE_ADDRESS,
          value: feeSats,
        }));
      }

      const result = await _walletInstance.send(outputs);
      disconnectElectrum();

      setState(s => ({ ...s, sending: false }));

      // Refresh balances shortly after — the send spent BCH (fee + dust) and
      // moved tokens.
      setTimeout(() => {
        if (_walletInstance) {
          getBalanceReliable(_walletInstance, true).then(notifyBalanceChange).catch(() => {});
          getTokenBalancesReliable(_walletInstance, true).catch(() => {});
        }
      }, 5000);

      return result.txId;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Token send failed';
      setState(s => ({ ...s, sending: false, error: msg }));
      throw err;
    }
  }, []);

  /** Back-compat wrapper: send PUSD specifically. */
  const sendPusd = useCallback((
    toAddress: string,
    amountBase: bigint,
    opts?: { feeSats?: bigint },
  ): Promise<string> => sendToken(toAddress, PUSD_CATEGORY, amountBase, opts), [sendToken]);

  /**
   * Send BCH to a single address.
   */
  const sendBCH = useCallback(async (toAddress: string, amountSats: bigint): Promise<string> => {
    if (!_walletInstance) throw new Error('Wallet not ready');

    // Recreate wallet from WIF before sending to ensure fresh UTXOs.
    // Don't disconnect Electrum here — send() reuses the same connection.
    try {
      const wif = _walletInstance.privateKeyWif;
      if (wif) {
        _walletInstance = await _mainnetModule.Wallet.fromWIF(wif);
      }
    } catch {}

    const balanceBefore = state.balanceSats;
    setState(s => ({ ...s, sending: true, error: null }));

    try {
      const result = await _walletInstance.send([{
        cashaddr: toAddress,
        value: amountSats,
      }]);
      disconnectElectrum(); // Kill Electrum AFTER send completes

      const estimatedRemaining = balanceBefore - amountSats - BigInt(300);
      const optimistic = estimatedRemaining > BigInt(0) ? estimatedRemaining : BigInt(0);
      const resultBalance = result.balance !== undefined ? parseSats(result.balance) : null;
      const immediateBalance = resultBalance !== null && resultBalance > BigInt(0) ? resultBalance : optimistic;

      notifyBalanceChange(immediateBalance, true); // trusted: send result
      setState(s => ({ ...s, sending: false }));

      // Single delayed balance refresh (not a retry loop)
      setTimeout(() => {
        if (_walletInstance) {
          getBalanceReliable(_walletInstance, true).then(sats => {
            notifyBalanceChange(sats);
          }).catch(() => {});
        }
      }, 5000);

      return result.txId;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Send failed';
      setState(s => ({ ...s, sending: false, error: msg }));
      throw err;
    }
  }, [state.balanceSats]);

  /**
   * Send BCH to multiple outputs in a single transaction.
   */
  const sendMultiOutput = useCallback(async (outputs: { cashaddr: string; value: bigint }[]): Promise<string> => {
    if (!_walletInstance) throw new Error('Wallet not ready');
    if (outputs.length === 0) throw new Error('No outputs provided');

    // Recreate wallet from WIF before sending to ensure fresh UTXOs.
    // Don't disconnect Electrum here — send() reuses the same connection.
    try {
      const wif = _walletInstance.privateKeyWif;
      if (wif) {
        _walletInstance = await _mainnetModule.Wallet.fromWIF(wif);
      }
    } catch {}

    const balanceBefore = state.balanceSats;
    setState(s => ({ ...s, sending: true, error: null }));

    try {
      const totalSpent = outputs.reduce((sum, o) => sum + o.value, BigInt(0));
      const result = await _walletInstance.send(outputs);
      disconnectElectrum(); // Kill Electrum AFTER send completes

      const estimatedRemaining = balanceBefore - totalSpent - BigInt(300);
      const optimistic = estimatedRemaining > BigInt(0) ? estimatedRemaining : BigInt(0);
      const resultBalance = result.balance !== undefined ? parseSats(result.balance) : null;
      const immediateBalance = resultBalance !== null && resultBalance > BigInt(0) ? resultBalance : optimistic;

      notifyBalanceChange(immediateBalance, true); // trusted: send result
      setState(s => ({ ...s, sending: false }));

      // Single delayed balance refresh (not a retry loop)
      setTimeout(() => {
        if (_walletInstance) {
          getBalanceReliable(_walletInstance, true).then(sats => {
            notifyBalanceChange(sats);
          }).catch(() => {});
        }
      }, 5000);

      return result.txId;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Send failed';
      setState(s => ({ ...s, sending: false, error: msg }));
      throw err;
    }
  }, [state.balanceSats]);

  /**
   * Send a tip with platform fee DEDUCTED from the payment amount.
   *
   * The user spends exactly `tipSats`. The fee is subtracted from inside:
   *   - Recipient receives: tipSats - fee
   *   - Platform receives:  fee (as separate output if >= dust limit)
   *   - If fee < dust:      fee is implicit (deducted from recipient,
   *                          becomes extra miner fee / retained value)
   *
   * Fee is always proportional: 1% of tipSats, no fixed minimums.
   */
  const sendTipWithFee = useCallback(async (recipientAddress: string, tipSats: bigint): Promise<string> => {
    if (!_walletInstance) throw new Error('Wallet not ready');

    // Pre-validate the recipient address before doing anything else.
    // This gives a clear error instead of a cryptic mainnet-js decode failure.
    if (!recipientAddress || !recipientAddress.startsWith('bitcoincash:')) {
      throw new Error('Invalid cashaddr: address must start with bitcoincash:');
    }
    // Quick CashAddr payload check (after prefix)
    const payload = recipientAddress.slice('bitcoincash:'.length);
    if (!/^[qp][qpzry9x8gf2tvdw0s3jn54khce6mua7l]{41}$/.test(payload)) {
      throw new Error('Invalid cashaddr: malformed payload');
    }

    // Recreate wallet from WIF before sending to ensure fresh UTXOs.
    // This prevents "mempool conflict" errors caused by stale UTXO cache.
    // NOTE: Do NOT disconnect Electrum here — send() reuses the connection
    // that fromWIF() establishes. Disconnecting would force a second
    // Electrum handshake and roughly double the send latency.
    try {
      const wif = _walletInstance.privateKeyWif;
      if (wif) {
        _walletInstance = await _mainnetModule.Wallet.fromWIF(wif);
      }
    } catch {}

    const balanceBefore = state.balanceSats;

    const fee = calculatePlatformFee(Number(tipSats));
    const feeSats = BigInt(fee);
    const netToRecipient = tipSats - feeSats;

    if (netToRecipient <= BigInt(0)) throw new Error('Tip amount too small');

    const outputs: { cashaddr: string; value: bigint }[] = [];
    if (canSendFeeOutput(fee)) {
      outputs.push({ cashaddr: recipientAddress, value: netToRecipient });
      outputs.push({ cashaddr: PLATFORM_FEE_ADDRESS, value: feeSats });
    } else {
      outputs.push({ cashaddr: recipientAddress, value: netToRecipient });
    }

    // ── Optimistic UI: update balance immediately, don't wait for broadcast ──
    const optimisticBalance = balanceBefore - tipSats - BigInt(300);
    const optimistic = optimisticBalance > BigInt(0) ? optimisticBalance : BigInt(0);
    notifyBalanceChange(optimistic, true); // trusted: user-initiated send
    setState(s => ({ ...s, sending: true, error: null }));

    try {
      const result = await _walletInstance.send(outputs);
      disconnectElectrum(); // Kill Electrum AFTER send — not before

      const resultBalance = result.balance !== undefined ? parseSats(result.balance) : null;
      if (resultBalance && resultBalance > BigInt(0)) {
        notifyBalanceChange(resultBalance, true); // trusted: send result
      }

      setState(s => ({ ...s, sending: false }));

      // Single delayed refresh — rate-limited so it won't spam
      setTimeout(() => {
        if (_walletInstance) {
          getBalanceReliable(_walletInstance, true).then(sats => {
            notifyBalanceChange(sats);
          }).catch(() => {});
        }
      }, 5000);

      return result.txId;
    } catch (err) {
      // Revert optimistic balance on failure
      notifyBalanceChange(balanceBefore, true); // trusted: reverting to known-good
      const msg = err instanceof Error ? err.message : 'Send failed';
      setState(s => ({ ...s, sending: false, error: msg }));
      throw err;
    }
  }, [state.balanceSats]);

  const getMaxSend = useCallback(async (): Promise<bigint> => {
    // Estimate max send from cached balance minus typical fee (~400 sats)
    // Avoids calling wallet.getMaxAmountToSend() which triggers Electrum
    const bal = _cachedBalanceSats > BigInt(0) ? _cachedBalanceSats : state.balanceSats;
    const fee = BigInt(400);
    return bal > fee ? bal - fee : BigInt(0);
  }, [state.balanceSats]);

  const getWIF = useCallback((): string | null => readCachedWIF(_currentWalletPubkey || undefined), []);

  // ── At-rest PIN encryption management ──────────────────────────────

  /** Refresh the `locked` / `encrypted` flags in state after a change. */
  const syncEncFlags = useCallback(() => {
    setState(s => ({ ...s, ...encFlags(_currentWalletPubkey) }));
  }, []);

  const encryptWallet = useCallback(async (pin: string) => {
    const pk = _currentWalletPubkey || undefined;
    // We must have the plaintext WIF available (wallet unlocked / plaintext).
    const wif = readCachedWIF(pk);
    if (!wif) throw new Error('Wallet is not unlocked — cannot encrypt.');
    await enableWIFEncryption(wif, pin, pk);
    // Note: we intentionally do NOT mirror to the legacy shared key — each
    // identity's wallet lives only under its per-pubkey slot.
    syncEncFlags();
  }, [syncEncFlags]);

  const decryptWallet = useCallback(async (pin: string) => {
    const pk = _currentWalletPubkey || undefined;
    await disableWIFEncryption(pin, pk);
    syncEncFlags();
  }, [syncEncFlags]);

  const changeWalletPin = useCallback(async (oldPin: string, newPin: string) => {
    const pk = _currentWalletPubkey || undefined;
    await changeWIFPin(oldPin, newPin, pk);
    syncEncFlags();
  }, [syncEncFlags]);

  const unlockWallet = useCallback(async (pin: string) => {
    const pk = _currentWalletPubkey || undefined;
    // Throws "Incorrect PIN." on a bad PIN (AES-GCM auth failure).
    await unlockWIF(pin, pk);
    // Now that the plaintext is cached in the session, (re)initialize.
    initRef.current = false;
    await init();
    syncEncFlags();
  }, [init, syncEncFlags]);

  const lockWallet = useCallback(() => {
    const pk = _currentWalletPubkey || undefined;
    if (!isWIFEncryptedStored(pk)) return; // nothing to lock (plaintext)
    lockWIFSession(pk);
    _walletInstance = null;
    initRef.current = false;
    _cachedBalanceSats = BigInt(0);
    _lastBalanceFetchTime = 0;
    setState(s => ({
      ...s, ready: false, loading: false, address: null, fullAddress: null,
      tokenAddress: null, fullTokenAddress: null,
      balanceSats: BigInt(0), locked: true, encrypted: true,
      pusdBase: BigInt(0),
      tokenBalances: {},
    }));
  }, []);

  const clearWallet = useCallback(() => {
    _walletInstance = null;
    initRef.current = false;
    // Reset in-memory balance so next user doesn't see stale value
    _cachedBalanceSats = BigInt(0);
    _lastBalanceFetchTime = 0;
    // Remove per-user WIF and balance cache
    clearCachedWIF(_currentWalletPubkey || undefined);
    clearCachedBalance(_currentWalletPubkey);
    // Also remove legacy key so it doesn't leak to other profiles
    try { localStorage.removeItem('bchnostr:tip-wallet-wif'); } catch {}
    try { localStorage.removeItem('bchnostr:tip-wallet-balance'); } catch {}
    // Publish an empty event to Nostr to overwrite the synced WIF
    // so it doesn't come back on re-login or other devices
    (async () => {
      try {
        const signer = (window as any).nostr;
        if (!signer) return;
        const pubkey = await signer.getPublicKey();
        const encrypted = await signer.nip44.encrypt(pubkey, '__DELETED__');
        const unsigned = {
          kind: 30078,
          content: encrypted,
          tags: [['d', NOSTR_D_TAG], ['alt', 'Wallet deleted']],
          created_at: Math.floor(Date.now() / 1000),
        };
        const signed = await signer.signEvent(unsigned);
        const relays = getReadRelays().slice(0, 4);
        await Promise.allSettled(
          relays.map(url => new Promise<void>(resolve => {
            try {
              const ws = new WebSocket(url);
              ws.onopen = () => { ws.send(JSON.stringify(['EVENT', signed])); setTimeout(() => { ws.close(); resolve(); }, 2000); };
              ws.onerror = () => resolve();
              setTimeout(() => { try { ws.close(); } catch {} resolve(); }, 5000);
            } catch { resolve(); }
          }))
        );
      } catch {}
    })();
    _currentWalletPubkey = null;
    notifyBalanceChange(BigInt(0), true); // trusted: wallet deletion
    setState({
      ready: false, loading: false, address: null, fullAddress: null,
      tokenAddress: null, fullTokenAddress: null,
      balanceSats: BigInt(0), sending: false, error: null,
      pusdBase: BigInt(0),
      tokenBalances: {},
      locked: false, encrypted: false,
    });
  }, []);

  const importWIF = useCallback(async (wif: string) => {
    // ── Sanitize input ─────────────────────────────────────────────
    // Mobile keyboards, clipboard managers, and browsers can add spaces,
    // newlines, zero-width spaces, smart quotes, and other invisible chars.
    // Keep ONLY valid Base58 characters (the alphabet used by WIF keys).
    const cleaned = wif.trim().replace(/[^1-9A-HJ-NP-Za-km-z]/g, '');

    if (!cleaned) {
      const err = new Error('Please paste a WIF private key');
      setState(s => ({ ...s, error: err.message }));
      throw err;
    }

    // WIF keys must start with K, L (compressed) or 5 (uncompressed)
    if (!/^[KL5][1-9A-HJ-NP-Za-km-z]+$/.test(cleaned)) {
      const hint = cleaned.startsWith('npub') || cleaned.startsWith('nsec')
        ? 'That looks like a Nostr key, not a BCH WIF key. WIF keys start with K, L, or 5.'
        : cleaned.startsWith('bitcoincash:') || cleaned.startsWith('q') || cleaned.startsWith('p')
          ? 'That looks like a BCH address, not a WIF key. You need the private key (starts with K, L, or 5).'
          : cleaned.startsWith('xprv') || cleaned.startsWith('xpub')
            ? 'That looks like an extended key. Please use the Seed Phrase tab instead, or export the WIF from your wallet app.'
            : `Invalid WIF key format. WIF keys start with K, L, or 5 (got "${cleaned.slice(0, 3)}...").`;
      const err = new Error(hint);
      setState(s => ({ ...s, error: hint }));
      throw err;
    }

    // Standard WIF is 51 (uncompressed) or 52 (compressed) characters.
    // We accept a generous range and let mainnet-js do the real cryptographic validation.
    if (cleaned.length < 50 || cleaned.length > 56) {
      const err = new Error(`WIF key should be around 51-52 characters (got ${cleaned.length} after cleanup). Make sure you copied the full key with no extra characters.`);
      setState(s => ({ ...s, error: err.message }));
      throw err;
    }

    console.log('[TipWallet] Importing WIF:', cleaned.slice(0, 4) + '...' + cleaned.slice(-4), `(${cleaned.length} chars, original: ${wif.trim().length} chars)`);

    _walletInstance = null;
    initRef.current = false;
    // Reset balance cache — the imported wallet has a different balance
    _cachedBalanceSats = BigInt(0);
    _lastBalanceFetchTime = 0;
    setState(s => ({ ...s, loading: true, error: null, ready: false, balanceSats: BigInt(0) }));

    try {
      await loadMainnet();
      const wallet = await _mainnetModule.Wallet.fromWIF(cleaned);
      _walletInstance = wallet;
      disconnectElectrum();

      const addr = wallet.getDepositAddress();
      const tokenAddr = deriveTokenAddress(wallet);
      console.log('[TipWallet] Import success. Address:', addr);

      // ── Persist locally FIRST so it survives app restarts ──────
      // Stored ONLY under the per-identity key — never the legacy shared
      // slot, which would leak this wallet into other accounts.
      cacheWIF(cleaned, _currentWalletPubkey || undefined);

      // ── Sync to Nostr relays so other devices get it ────────────
      // Await (don't fire-and-forget) so we're confident the relay
      // has the imported WIF before the user navigates away.
      if ((window as any).nostr) {
        try {
          await publishWIFToNostr(cleaned);
          console.log('[TipWallet] WIF synced to Nostr relays');
        } catch (e) {
          console.warn('[TipWallet] Relay sync failed (wallet still saved locally):', e);
        }
      }

      const balanceSats = await getBalanceReliable(wallet, true);
      initRef.current = true;
      setState({
        ready: true, loading: false,
        address: addr.replace(/^bitcoincash:/i, ''),
        fullAddress: addr,
        tokenAddress: tokenAddr ? tokenAddr.replace(/^bitcoincash:/i, '') : null,
        fullTokenAddress: tokenAddr,
        balanceSats, pusdBase: _cachedPusdBase, tokenBalances: _cachedTokenBalances, sending: false, error: null,
        ...encFlags(_currentWalletPubkey),
      });
      notifyBalanceChange(balanceSats, true);
      getTokenBalancesReliable(wallet, true).catch(() => {});
    } catch (err) {
      const raw = err instanceof Error ? err.message : String(err);
      // Make mainnet-js decode errors friendlier while preserving the raw detail
      const friendly = raw.includes('fetch') || raw.includes('network') || raw.includes('Failed to fetch')
        ? 'Network error loading wallet library. Check your internet connection and try again.'
        : raw.includes('decode') || raw.includes('checksum') || raw.includes('Invalid') || raw.includes('non-base32') || raw.includes('payload')
          ? `Invalid WIF key — the key didn't pass cryptographic validation. Make sure you copied it exactly with no missing or extra characters. (${raw.slice(0, 80)})`
          : raw;
      console.error('[TipWallet] Import failed:', raw);
      setState(s => ({
        ...s, loading: false,
        error: friendly,
      }));
      throw new Error(friendly);
    }
  }, []);

  const importMnemonic = useCallback(async (mnemonic: string, derivationPath?: string) => {
    const words = mnemonic.trim().toLowerCase().split(/\s+/);
    if (words.length !== 12 && words.length !== 24) {
      const err = new Error(`Seed phrase must be 12 or 24 words (got ${words.length})`);
      setState(s => ({ ...s, error: err.message }));
      throw err;
    }

    _walletInstance = null;
    initRef.current = false;
    setState(s => ({ ...s, loading: true, error: null, ready: false }));
    try {
      await loadMainnet();
      const { Wallet } = _mainnetModule;

      // Use the standard BIP44 BCH derivation path: m/44'/145'/0'/0/0
      const path = derivationPath || "m/44'/145'/0'/0/0";
      const cleanMnemonic = words.join(' ');

      // mainnet-js Wallet.fromSeed expects the mnemonic and optional derivation path
      const wallet = await Wallet.fromSeed(cleanMnemonic, path);
      _walletInstance = wallet;
      disconnectElectrum();

      // Get the WIF from the derived wallet so we can store it
      const wif = wallet.privateKeyWif;
      cacheWIF(wif, _currentWalletPubkey || undefined);
      if ((window as any).nostr) publishWIFToNostr(wif);

      const addr = wallet.getDepositAddress();
      const tokenAddr = deriveTokenAddress(wallet);
      const balanceSats = await getBalanceReliable(wallet, true);
      initRef.current = true;
      setState({
        ready: true, loading: false,
        address: addr.replace(/^bitcoincash:/i, ''),
        fullAddress: addr,
        tokenAddress: tokenAddr ? tokenAddr.replace(/^bitcoincash:/i, '') : null,
        fullTokenAddress: tokenAddr,
        balanceSats, pusdBase: _cachedPusdBase, tokenBalances: _cachedTokenBalances, sending: false, error: null,
        ...encFlags(_currentWalletPubkey),
      });
      getTokenBalancesReliable(wallet, true).catch(() => {});
    } catch (err) {
      setState(s => ({
        ...s, loading: false,
        error: err instanceof Error ? err.message : 'Invalid seed phrase',
      }));
      throw err;
    }
  }, []);

  const generateNewWallet = useCallback(async () => {
    _walletInstance = null;
    initRef.current = false;
    setState(s => ({ ...s, loading: true, error: null, ready: false }));
    try {
      await loadMainnet();
      const { Wallet } = _mainnetModule;
      const newWallet = await Wallet.newRandom();
      const wif = newWallet.privateKeyWif;
      _walletInstance = newWallet;
      disconnectElectrum();

      // Get current user pubkey
      let currentPubkey: string | null = null;
      try {
        if ((window as any).nostr) currentPubkey = await (window as any).nostr.getPublicKey();
      } catch {}
      _currentWalletPubkey = currentPubkey;

      // Store locally and sync to Nostr
      cacheWIF(wif, currentPubkey || undefined);
      // Also remove legacy key
      try { localStorage.removeItem('bchnostr:tip-wallet-wif'); } catch {}
      if ((window as any).nostr) publishWIFToNostr(wif);

      const addr = newWallet.getDepositAddress();
      const tokenAddr = deriveTokenAddress(newWallet);
      initRef.current = true;
      setState({
        ready: true, loading: false,
        address: addr.replace(/^bitcoincash:/i, ''),
        fullAddress: addr,
        tokenAddress: tokenAddr ? tokenAddr.replace(/^bitcoincash:/i, '') : null,
        fullTokenAddress: tokenAddr,
        balanceSats: BigInt(0), sending: false, error: null,
        pusdBase: BigInt(0),
        tokenBalances: {},
        ...encFlags(currentPubkey),
      });
    } catch (err) {
      setState(s => ({
        ...s, loading: false,
        error: err instanceof Error ? err.message : 'Failed to generate wallet',
      }));
    }
  }, []);

  return {
    ...state,
    init,
    refreshBalance,
    refreshPusdBalance,
    refreshTokenBalances,
    sendPusd,
    sendToken,
    sendBCH,
    sendMultiOutput,
    sendTipWithFee,
    getMaxSend,
    getWIF,
    clearWallet,
    generateNewWallet,
    importWIF,
    importMnemonic,
    encryptWallet,
    decryptWallet,
    unlockWallet,
    changeWalletPin,
    lockWallet,
    // hasWallet is true if: wallet is ready in this session OR there's a stored
    // WIF in localStorage — including a locked (encrypted) one, which
    // readCachedWIF would report as null. Check the raw stored value too.
    hasWallet: !!_walletInstance
      || !!readCachedWIF(_currentWalletPubkey || undefined)
      || !!readRawWIF(_currentWalletPubkey || undefined),
  };
}

